import { motion } from "framer-motion";
import { ArrowUpRight, BookOpen, Leaf, Store } from "lucide-react";
import { PROGRAMS } from "@/lib/data";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { useScrollTo } from "@/hooks/useLenis";

const ICONS = { BookOpen, Leaf, Store };

const ImageTile = ({ p }) => (
  <motion.article
    whileHover={{ y: -6 }}
    transition={{ duration: 0.35, ease: "easeOut" }}
    className="group relative h-full min-h-[260px] overflow-hidden rounded-[2rem] bg-navy"
    data-testid={`program-${p.key}`}
  >
    <img src={p.image} alt={p.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
    <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/30 to-transparent" />
    <span className="eyebrow absolute left-6 top-6 rounded-full bg-sand-50/90 px-3 py-1.5 text-navy">{p.tag}</span>
    <div className="absolute inset-x-6 bottom-6 text-sand-50">
      <h3 className="font-display text-2xl leading-tight sm:text-3xl">{p.title}</h3>
      <p className="mt-2 max-w-md text-sm text-sand-50/75">{p.desc}</p>
    </div>
  </motion.article>
);

const TextTile = ({ p }) => {
  const Icon = ICONS[p.icon];
  return (
    <motion.article
      whileHover={{ y: -6 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="flex h-full min-h-[220px] flex-col justify-between rounded-[2rem] border border-navy/10 bg-white/80 p-7 transition-colors duration-300 hover:border-gold"
      data-testid={`program-${p.key}`}
    >
      <div className="flex items-center justify-between">
        <span className="grid h-11 w-11 place-items-center rounded-full bg-navy text-gold">
          <Icon className="h-5 w-5" />
        </span>
        <span className="eyebrow text-teal">{p.tag}</span>
      </div>
      <div className="mt-8">
        <h3 className="font-display text-2xl leading-tight text-navy">{p.title}</h3>
        <p className="mt-2 text-sm text-slate-600">{p.desc}</p>
      </div>
    </motion.article>
  );
};

const CtaTile = ({ p, onClick }) => (
  <motion.button
    whileHover={{ y: -6 }}
    transition={{ duration: 0.35, ease: "easeOut" }}
    onClick={onClick}
    className="group flex h-full min-h-[220px] w-full flex-col justify-between rounded-[2rem] bg-gold p-7 text-left text-navy transition-colors duration-300 hover:bg-amber"
    data-testid="program-cta"
  >
    <span className="grid h-11 w-11 place-items-center rounded-full bg-navy text-gold transition-transform duration-300 group-hover:rotate-45">
      <ArrowUpRight className="h-5 w-5" />
    </span>
    <span>
      <span className="block font-display text-2xl leading-tight">{p.title}</span>
      <span className="mt-2 block text-sm text-navy/70">{p.desc}</span>
    </span>
  </motion.button>
);

export const Programs = () => {
  const scrollTo = useScrollTo();
  return (
    <section id="program" className="relative bg-sand-100 py-28 lg:py-36" data-testid="programs-section">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading
            eyebrow="Kegiatan & Program"
            title={
              <>
                Memakmurkan masjid, <em>memakmurkan</em> pesisir.
              </>
            }
          />
          <Reveal delay={0.2} className="max-w-sm text-sm leading-relaxed text-slate-600 md:text-base">
            Dari manasik haji hingga balap jukung—program Masjid Pantai Bali dirancang dengan semangat 5E agar manfaatnya
            menjangkau jamaah, nelayan, pelaku UMKM, dan wisatawan.
          </Reveal>
        </div>

        <div className="mt-16 grid gap-4 md:grid-cols-6 md:auto-rows-[230px]" data-testid="programs-grid">
          {PROGRAMS.map((p, i) => (
            <Reveal key={p.key} delay={(i % 3) * 0.08} className={p.span}>
              {p.type === "image" ? <ImageTile p={p} /> : p.type === "cta" ? <CtaTile p={p} onClick={() => scrollTo("#kontak")} /> : <TextTile p={p} />}
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
